import { useState } from "react"
import { Link } from "@inertiajs/react"
import { Check, ChevronDown } from "lucide-react"


// ProgressBar is the top-of-page stepper. It renders:
//   - desktop: a horizontal list with numbered/checked chips per step
//   - mobile: a compact "Step N of M" label with a tap-to-expand sheet
// Done steps are clickable (navigate back). The current step is
// highlighted but not a link.
//
// Everything here comes from the server's `wizard` prop
// (Planning::IntakeWizard#state): each step carries its label and its
// done flag. This file derives nothing — "done" is NOT "before the current
// index", which is how it used to render and why welcome + audit_intro
// showed as done even though the user had never seen them.
export function ProgressBar({ wizard }) {
  const { steps, current: currentStep } = wizard
  const [mobileOpen, setMobileOpen] = useState(false)
  const currentIdx = steps.findIndex((s) => s.key === currentStep)
  const pct = Math.round(((currentIdx + 1) / steps.length) * 100)

  const renderChip = (step, idx, { compact = false } = {}) => {
    const isCurrent = step.key === currentStep
    const isDone = step.done
    const chipContent = (
      <>
        <div
          className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-semibold ${
            isCurrent
              ? "bg-kf-red text-white"
              : isDone
              ? "bg-kf-red/15 text-kf-red group-hover:bg-kf-red/25"
              : "bg-zinc-200 text-zinc-500"
          }`}
        >
          {isDone && !isCurrent ? <Check className="h-3 w-3" /> : idx + 1}
        </div>
        <span
          className={
            isCurrent
              ? "font-semibold text-zinc-900"
              : isDone
              ? "text-zinc-700 group-hover:text-kf-red"
              : "text-zinc-400"
          }
        >
          {step.label}
        </span>
      </>
    )

    // Done (but not current) → clickable back-nav via GET. Everything
    // else (current, or future/unreached) is a plain element.
    if (isDone && !isCurrent) {
      return (
        <Link
          key={step.key}
          href={`/app/plan/intake?step=${step.key}`}
          preserveScroll
          className={`group flex items-center gap-2 shrink-0 ${compact ? "py-2 px-2 rounded hover:bg-zinc-50 w-full" : ""}`}
        >
          {chipContent}
        </Link>
      )
    }

    return (
      <div
        key={step.key}
        className={`flex items-center gap-2 shrink-0 ${compact ? "py-2 px-2 w-full" : ""}`}
      >
        {chipContent}
      </div>
    )
  }

  return (
    <div>
      {/* Compact mobile indicator — expands to a tap-friendly list */}
      <div className="sm:hidden mb-3">
        <button
          type="button"
          onClick={() => setMobileOpen((o) => !o)}
          aria-expanded={mobileOpen}
          aria-label="Toggle step list"
          className="w-full flex items-center justify-between text-xs text-zinc-600 mb-1.5"
        >
          <span>
            Step {currentIdx + 1} of {steps.length}:{" "}
            <strong className="text-zinc-900">{steps[currentIdx]?.label}</strong>
          </span>
          <span className="flex items-center gap-1">
            {pct}%
            <ChevronDown
              className={`w-3.5 h-3.5 transition-transform ${mobileOpen ? "rotate-180" : ""}`}
            />
          </span>
        </button>
        <div className="h-1.5 w-full bg-zinc-200 rounded-full overflow-hidden">
          <div
            className="h-full bg-kf-red transition-all"
            style={{ width: `${pct}%` }}
          />
        </div>
        {mobileOpen && (
          <div className="mt-3 rounded-md border border-zinc-200 bg-white divide-y divide-zinc-100">
            {steps.map((step, idx) => renderChip(step, idx, { compact: true }))}
          </div>
        )}
      </div>

      {/* Desktop stepper — clickable for completed steps */}
      <div className="hidden sm:flex items-center gap-2 text-xs mb-4 overflow-x-auto">
        {steps.map((step, idx) => (
          <div key={step.key} className="flex items-center gap-2 shrink-0">
            {renderChip(step, idx)}
            {idx < steps.length - 1 && (
              <span className="text-zinc-300">•</span>
            )}
          </div>
        ))}
      </div>
    </div>
  )
}
